import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { ExportDialog } from '../components/ExportDialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Handshake, TrendingUp, CheckCircle2, Clock, XCircle, Loader2,
  FileSpreadsheet, Filter, Calendar, Users,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const PartnersDashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Multi-select
  const [selectedPartners, setSelectedPartners] = useState([]);

  // Export
  const [showExport, setShowExport] = useState(false);
  const [exporting, setExporting] = useState(false);

  const fetchData = async () => {
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const res = await axios.get(`${API_URL}/dashboard/partners`, { params });
      setData(res.data);
    } catch (err) {
      console.error(err);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [startDate, endDate]);

  const togglePartner = (id) => {
    setSelectedPartners(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const toggleAll = () => {
    if (!data) return;
    const allIds = data.partners.map(p => p.partner_id);
    setSelectedPartners(prev =>
      prev.length === allIds.length ? [] : allIds
    );
  };

  const handleExport = async (columns) => {
    const ids = selectedPartners.length > 0
      ? selectedPartners
      : (data?.partners || []).map(p => p.partner_id);

    if (ids.length === 0) { toast.error('Seleccione pelo menos um parceiro'); return; }
    setExporting(true);
    try {
      const res = await axios.post(`${API_URL}/production/export-excel`, {
        type: 'partners',
        entity_ids: ids,
        columns,
        start_date: startDate || null,
        end_date: endDate || null,
      }, { responseType: 'blob' });

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const disp = res.headers['content-disposition'];
      const fname = disp ? disp.split('filename="')[1]?.replace('"', '') : 'producao_parceiros.xlsx';
      link.setAttribute('download', fname);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel exportado');
      setShowExport(false);
    } catch (err) {
      console.error(err);
      toast.error('Erro ao exportar');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  const partners = data?.partners || [];
  const totals = data?.totals || {};

  return (
    <Layout>
      <div className="space-y-6" data-testid="partners-dashboard">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-outfit flex items-center gap-2">
              <Handshake className="w-6 h-6 text-[#2D3E6F]" />
              Producao de Parceiros
            </h1>
            <p className="text-slate-500 mt-1">{partners.length} parceiros</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-slate-400" />
              <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="h-9 w-36" />
              <span className="text-slate-400">-</span>
              <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="h-9 w-36" />
            </div>
            <Button
              onClick={() => setShowExport(true)}
              className="bg-emerald-600 hover:bg-emerald-700 rounded-full gap-2"
              data-testid="export-partners-excel-btn"
            >
              <FileSpreadsheet className="w-4 h-4" />
              Exportar Excel {selectedPartners.length > 0 ? `(${selectedPartners.length})` : ''}
            </Button>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card className="rounded-2xl border-slate-100">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-[#2D3E6F]">{totals.total_contracts || 0}</p>
              <p className="text-xs text-slate-500 mt-1">Total Contratos</p>
            </CardContent>
          </Card>
          <Card className="rounded-2xl border-slate-100">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-emerald-600">{totals.by_status?.concluido || 0}</p>
              <p className="text-xs text-slate-500 mt-1">Concluidos</p>
            </CardContent>
          </Card>
          <Card className="rounded-2xl border-slate-100">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-amber-600">{totals.by_status?.pendente || 0}</p>
              <p className="text-xs text-slate-500 mt-1">Pendentes</p>
            </CardContent>
          </Card>
          <Card className="rounded-2xl border-slate-100">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-slate-600">{partners.length}</p>
              <p className="text-xs text-slate-500 mt-1">Parceiros</p>
            </CardContent>
          </Card>
        </div>

        {/* Partners Table */}
        <Card className="rounded-2xl border-slate-100 overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/50">
                  <TableHead className="w-10">
                    <Checkbox
                      checked={selectedPartners.length === partners.length && partners.length > 0}
                      onCheckedChange={toggleAll}
                      data-testid="select-all-partners"
                    />
                  </TableHead>
                  <TableHead className="font-semibold">Parceiro</TableHead>
                  <TableHead className="font-semibold text-center">Membros</TableHead>
                  <TableHead className="font-semibold text-center">Total</TableHead>
                  <TableHead className="font-semibold text-center">Concluidos</TableHead>
                  <TableHead className="font-semibold text-center">Pendentes</TableHead>
                  <TableHead className="font-semibold text-center">Anulados</TableHead>
                  <TableHead className="w-24"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {partners.map(p => (
                  <TableRow
                    key={p.partner_id}
                    className={`hover:bg-slate-50/80 transition-colors ${selectedPartners.includes(p.partner_id) ? 'bg-blue-50/50' : ''}`}
                    data-testid={`partner-dash-row-${p.partner_id}`}
                  >
                    <TableCell>
                      <Checkbox
                        checked={selectedPartners.includes(p.partner_id)}
                        onCheckedChange={() => togglePartner(p.partner_id)}
                      />
                    </TableCell>
                    <TableCell>
                      <span className="font-medium text-[#2D3E6F]">{p.partner_name}</span>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="flex items-center justify-center gap-1 text-sm">
                        <Users className="w-3.5 h-3.5 text-slate-400" /> {p.team_count || 0}
                      </span>
                    </TableCell>
                    <TableCell className="text-center font-semibold">{p.total_contracts}</TableCell>
                    <TableCell className="text-center">
                      <span className="inline-flex items-center gap-1 text-emerald-600 font-medium">
                        <CheckCircle2 className="w-3.5 h-3.5" /> {p.by_status?.concluido || 0}
                      </span>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="inline-flex items-center gap-1 text-amber-600 font-medium">
                        <Clock className="w-3.5 h-3.5" /> {p.by_status?.pendente || 0}
                      </span>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="inline-flex items-center gap-1 text-red-500 font-medium">
                        <XCircle className="w-3.5 h-3.5" /> {p.by_status?.anulado || 0}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-full text-xs"
                        onClick={() => navigate(`/dashboard/parceiros/${p.partner_id}`)}
                        data-testid={`view-partner-${p.partner_id}`}
                      >
                        Ver detalhe
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      </div>

      <ExportDialog
        open={showExport}
        onClose={setShowExport}
        onExport={handleExport}
        exporting={exporting}
        title={`Exportar Producao ${selectedPartners.length > 0 ? `(${selectedPartners.length} parceiros)` : '(Todos)'}`}
      />
    </Layout>
  );
};
